import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthLayout, Button, Card, ErrorMessage, Field, Input } from '../components/ui.tsx';
import { configApi, platformApi } from '../lib/api.ts';
import { authClient } from '../lib/auth-client.ts';

/**
 * Selector de organización.
 *
 * Elegir organización **reescribe la sesión** en el servidor
 * (`setActive`), no guarda nada en el navegador. Es deliberado: el middleware
 * de la API saca la organización de la sesión y nunca de una cabecera, así que
 * si esto se guardara en el cliente, cambiar un valor en el navegador sería un
 * intento de acceder a datos de otra organización.
 */
export function OrganizationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: organizations, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data } = await authClient.organization.list();
      return data ?? [];
    },
  });

  // Con el registro por invitación, las organizaciones las da de alta quien
  // administra la plataforma. A los demás no se les enseña un formulario que el
  // servidor va a rechazar. Mientras no se sabe, tampoco.
  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get, staleTime: Infinity });
  const { data: platform } = useQuery({ queryKey: ['platform', 'me'], queryFn: platformApi.me });
  const canCreate = config?.registrationMode === 'open' || platform?.isPlatformAdmin === true;

  /**
   * El slug forma parte de la URL pública de la organización, así que tiene que
   * ser estable y legible. Se deriva del nombre quitando acentos: "Periódicos
   * del Caribe" da "periodicos-del-caribe" y no "peri-dicos-del-caribe".
   */
  function slugify(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48);
  }

  async function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    const slug = slugify(name);
    // Un sufijo corto evita la colisión entre dos organizaciones con el mismo
    // nombre, que en un grupo de medios es bastante probable.
    const unique = `${slug}-${Math.random().toString(36).slice(2, 7)}`;

    const { data, error: createError } = await authClient.organization.create({
      name,
      slug: unique,
    });

    if (createError || !data) {
      setBusy(false);
      setError(createError?.message ?? t('errors.generic'));
      return;
    }

    await enter(data.id);
  }

  async function enter(organizationId: string) {
    setBusy(true);
    const { error: activateError } = await authClient.organization.setActive({ organizationId });
    setBusy(false);

    if (activateError) {
      setError(activateError.message ?? t('errors.generic'));
      return;
    }

    await queryClient.invalidateQueries();
    await navigate({ to: '/' });
  }

  return (
    <AuthLayout>
      <Card title={t('org.chooser.title')}>
        <p className="mb-5 text-sm text-[var(--color-muted)]">{t('org.chooser.subtitle')}</p>

        <ErrorMessage>{error}</ErrorMessage>

        {isLoading ? (
          <p className="text-sm text-[var(--color-muted)]">{t('common.loading')}</p>
        ) : organizations && organizations.length > 0 ? (
          <ul className="mb-6 space-y-2">
            {organizations.map((org) => (
              <li key={org.id}>
                <button
                  type="button"
                  onClick={() => void enter(org.id)}
                  disabled={busy}
                  className="w-full rounded-lg border border-[var(--color-line)] px-4 py-3 text-left text-sm hover:bg-[var(--color-surface)] disabled:opacity-50"
                >
                  <span className="font-medium">{org.name}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-6 text-sm leading-relaxed text-[var(--color-muted)]">
            {canCreate ? t('org.chooser.none') : t('org.chooser.noneInvitationOnly')}
          </p>
        )}

        {canCreate ? (
          <form onSubmit={handleCreate} className="border-t border-[var(--color-line)] pt-5">
            <Field id="org-name" label={t('org.chooser.nameLabel')}>
              <Input
                id="org-name"
                required
                placeholder={t('org.chooser.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>

            <Button type="submit" loading={busy} disabled={name.trim().length === 0}>
              {busy ? t('org.chooser.creating') : t('org.chooser.create')}
            </Button>
          </form>
        ) : (
          // Sin organización y sin poder crear una, esta pantalla no tiene
          // salida: se ofrece al menos la de cerrar sesión.
          <Button
            type="button"
            variant="ghost"
            onClick={async () => {
              await authClient.signOut();
              await navigate({ to: '/entrar' });
            }}
          >
            {t('common.signOut')}
          </Button>
        )}
      </Card>
    </AuthLayout>
  );
}
